import { ShieldCheck, Zap, TrendingUp } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

const features = [
    {
        icon: Zap,
        title: "Rapid Deployment",
        desc: "Go from concept to production in weeks, not months."
    },
    {
        icon: ShieldCheck,
        title: "Trusted Security",
        desc: "Robust security measures designed to protect your data and ensure privacy."
    },
    {
        icon: TrendingUp,
        title: "Extended Partner",
        desc: "We work with your teams to constantly find automations that can be improved."
    }
];

const FeatureCard = ({ icon: Icon, title, desc, index }: { icon: typeof Zap; title: string; desc: string; index: number }) => {
    const { ref, isVisible } = useScrollReveal();

    return (
        <div
            ref={ref}
            className={`group p-8 rounded-2xl border border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all duration-500 ${isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 100}ms` }}
        >
            <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center mb-6 border border-white/[0.05]">
                <Icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
            <p className="text-muted-foreground text-base leading-relaxed font-light">{desc}</p>
        </div>
    );
};

const ValuePropSection = () => {
    const header = useScrollReveal();

    return (
        <section id="value-prop" className="py-24 relative overflow-hidden scroll-mt-32 section-glow">
            <div className="container px-4 md:px-6 relative z-10">
                <div
                    ref={header.ref}
                    className={`text-center mb-20 max-w-3xl mx-auto ${header.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                >
                    <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-[1.1] tracking-[-0.05em]">
                        Why enterprises{' '}
                        <span className="text-primary/40">work with Antz</span>
                    </h2>
                    <p className="text-muted-foreground text-lg md:text-xl font-light leading-relaxed">
                        New AI tools ship every week. Most of them won't matter to you. We test what's coming out, figure out what actually works in practice, and help you pick the right stack -- so you spend money on things that deliver, not things that just demo well.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    {features.map((feature, i) => (
                        <FeatureCard key={i} {...feature} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ValuePropSection;
